import { ChangeDetectionStrategy, Component, effect, input, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Friendship, PetDTO, Pronouns, Shop, User, UserProfile } from 'src/app/dto';
import { AchievementService } from 'src/app/services/achievement.service';
import { ApplicationService } from 'src/app/services/application.service';
import { ChatbarService } from 'src/app/services/chatbar.service';
import { CustomizationService } from 'src/app/services/customization/customization.service';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { InboxService } from 'src/app/services/inbox.service';
import { MetadataService } from 'src/app/services/metadata.service';
import { PetService } from 'src/app/services/pet.service';
import { ShopService } from 'src/app/services/shop.service';
import { CustomizationPickerComponent, CustomizationPickerItem } from '../customization-picker/customization-picker.component';
import { GenericModalComponent } from '../ui/generic-modal/generic-modal.component';
import { PageComponent } from '../base.component';
import { MainpageComponent } from '../mainpage/mainpage.component';





@Component({
    selector: 'app-user',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileComponent extends PageComponent {

    public static route = 'user';

    username = input<string>();

    // Every field below is set inside an async subscribe callback in initialize() (an HTTP fetch
    // or an external service's BehaviorSubject), not from a template-bound handler - each is
    // backed by a private signal, with a getter/setter pair keeping every existing this.field
    // read/write site (including the [(ngModel)] two-way bindings that mutate user/userProfile's
    // own nested properties in place) working unchanged.
    private userState = signal<User>(undefined);
    set user(value: User) { this.userState.set(value); }
    get user(): User { return this.userState(); }

    private userAchievementsState = signal<number[]>([]);
    set userAchievements(value: number[]) { this.userAchievementsState.set(value); }
    get userAchievements(): number[] { return this.userAchievementsState(); }

    private userPetsState = signal<PetDTO[]>([]);
    set userPets(value: PetDTO[]) { this.userPetsState.set(value); }
    get userPets(): PetDTO[] { return this.userPetsState(); }

    private userProfileState = signal<UserProfile>(undefined);
    set userProfile(value: UserProfile) { this.userProfileState.set(value); }
    get userProfile(): UserProfile { return this.userProfileState(); }

    private friendshipState = signal<Friendship>(undefined);
    set friendship(value: Friendship) { this.friendshipState.set(value); }
    get friendship(): Friendship { return this.friendshipState(); }

    private userShopsState = signal<Shop[]>([]);
    set userShops(value: Shop[]) { this.userShopsState.set(value); }
    get userShops(): Shop[] { return this.userShopsState(); }

    private viewedUserIsMeState = signal(false);
    set viewedUserIsMe(value: boolean) { this.viewedUserIsMeState.set(value); }
    get viewedUserIsMe(): boolean { return this.viewedUserIsMeState(); }

    editing: boolean = false;

    public countriesSignal: Signal<string[]>;
    public pronouns: Pronouns[] = Pronouns.getAll();



    constructor(private coreAppService: ApplicationService, private shopService: ShopService, private chatService: ChatbarService, private petService: PetService, private inboxService: InboxService,
        private router: Router, private achievementService: AchievementService, private metadataService: MetadataService,
        private customizationService: CustomizationService, public featureFlagService: FeatureFlagService) {
        super(coreAppService.authService, coreAppService.uiStateService);

        this.countriesSignal = toSignal(this.metadataService.getCountries(), { initialValue: [] });

        // Re-initializes on either the route's username changing or activeUser resolving/changing
        // (auth resolves asynchronously after this component constructs - see AuthService's
        // autologin flow - so the first run here can fire before the real activeUser is known;
        // reading activeUser() as a tracked dependency, not just once, is what re-runs this once
        // it resolves). Replaces two separate subscriptions (one per source) that existed before
        // this was converted to a signal input - effect() naturally covers the union of both.
        effect((onCleanup) => {
            const username = this.username();
            const activeUser = this.activeUser();
            if (username) {
                // Composed via a parent Subscription (rather than the tracked this.subscribe()
                // helper) and disposed through onCleanup: this effect re-runs on every username
                // change (viewing a different user's profile under this app's route reuse
                // strategy), and this.subscribe() only tears down at component destroy. Without
                // this, the previous profile's activeUserPets/activeUserShops/allFriendAndReqs$
                // subscriptions (multi-emission BehaviorSubjects, not one-shot HTTP calls) would
                // keep running and could overwrite this.userPets/userShops/friendship with stale
                // data - visibly, if that earlier profile happened to be the viewer's own (the
                // isMe branch), since activeUserPets/activeUserShops are the logged-in user's own
                // data and can emit again at any time from elsewhere in the app.
                const sub = this.initialize(username, activeUser);
                onCleanup(() => sub.unsubscribe());
            }
        });
    }

    private initialize(userNameParam: string, activeUser: User): Subscription {
        const sub = new Subscription();
        sub.add(this.coreAppService.userService.retrieveUser(userNameParam)
            .pipe(
                catchError((e, caught) => {
                    if (e.status === 404) {
                        this.router.navigate([`/${MainpageComponent.route}`]);
                    }
                    return throwError(() => e);
                }))
            .subscribe(retrievedUser => {
                this.user = retrievedUser;
                sub.add(this.coreAppService.userService.retrieveUserProfile(this.user.Username).subscribe(um => this.userProfile = um));

                if (this.isMe(activeUser)) {
                    sub.add(this.petService.activeUserPets.subscribe(pds => this.userPets = pds));
                    sub.add(this.shopService.activeUserShops.subscribe(shops => this.userShops = shops));
                    sub.add(this.achievementService.getAchievements(this.user.Username).subscribe(achieves => this.userAchievements = achieves));

                } else {
                    if (this.isAuthenticated()) {
                        sub.add(this.inboxService.allFriendAndReqs$.subscribe(allFriends => this.findFriendshipWithProfileUser(allFriends, this.user.Username)));
                    }
                    sub.add(this.petService.getPetsForUser(this.user.Username).subscribe(pds => this.userPets = pds));
                    sub.add(this.shopService.getUserShops(this.user.Username).subscribe(uss => this.userShops = uss));
                    sub.add(this.achievementService.getAchievements(this.user.Username).subscribe(achieves => this.userAchievements = achieves));

                }
            }));
        return sub;
    }

    isMe(activeUser: User) {
        this.viewedUserIsMe = false;
        if (activeUser && this.user) {
            this.viewedUserIsMe = activeUser.Username === this.user.Username;
        }
        return this.viewedUserIsMe;
    }

    updateUserProfile() {
        this.subscribe(this.coreAppService.userService.updateUserProfile(this.userProfile), () => { });
        this.subscribe(this.coreAppService.userService.updateUser(this.user), () => { });

        this.editing = false;
    }

    sendMessage(friendUsername: string): void {
        this.chatService.FindOrCreateAndOpenChat(friendUsername);
    }


    addFriend(friendUsername: string): void {
        this.inboxService.addFriend(friendUsername)
            .subscribe();
    }

    removeFriend(friendUsername: string): void {
        this.inboxService.removeFriend(friendUsername)
            .subscribe();
    }

    findFriendshipWithProfileUser(friends: Friendship[], profileUsername: string): void {
        this.friendship = friends.find(fsd => fsd.FriendUsername.localeCompare(profileUsername) === 0);
    }

    calculateAge(birthDate: Date): number {
        const now = new Date();

        return Math.floor((Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) - Date.UTC(birthDate.getFullYear(), birthDate.getMonth(), birthDate.getDate())) / (1000 * 60 * 60 * 24 * 365.2422));
    }

    openAvatarPicker(): void {
        if (!this.viewedUserIsMe) { return; }
        this.customizationService.initializeOwnedAvatars();
        this.coreAppService.uiStateService.openModalWithComponent(
            { size: 'lg' }, null, 'Select Profile Image', CustomizationPickerComponent, GenericModalComponent,
            (picker: CustomizationPickerComponent) => {
                picker.equippedId.set(this.userProfile.Image?.Id ?? null);
                // Every account owns the guaranteed "Default" image (see AuthService.CreateAccount),
                // so there's no "unequip to nothing" state worth offering here.
                picker.allowDefault = false;
                picker.roundImages = true;
                this.subscribe(this.customizationService.OwnedAvatars, owned => picker.items.set(owned as unknown as CustomizationPickerItem[]));
                picker.onSelect = item => this.customizationService.equipAvatar(item as any);
            });
    }

    openProfileBackgroundPicker(): void {
        if (!this.viewedUserIsMe) { return; }
        this.customizationService.initializeOwnedProfileBackgrounds();
        this.coreAppService.uiStateService.openModalWithComponent(
            { size: 'lg' }, null, 'Select Profile Background', CustomizationPickerComponent, GenericModalComponent,
            (picker: CustomizationPickerComponent) => {
                picker.equippedId.set(this.userProfile.ProfileBackground?.Id ?? null);
                picker.defaultLabel = 'No Background';
                // Matches the profile banner's own fallback gradient (see .blue-red-gradient in
                // profile.component.html) - unequipping just reveals the same gradient again.
                picker.defaultPreviewClass = 'blue-red-gradient';
                this.subscribe(this.customizationService.OwnedProfileBackgrounds, owned => picker.items.set(owned as unknown as CustomizationPickerItem[]));
                picker.onSelect = item => this.customizationService.equipProfileBackground(item as any);
            });
    }

}
